import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useAuth } from "@/hooks/useAuth"
import { useWhatsAppConnection } from "@/hooks/useWhatsApp"
import { Loader2, MessageCircle, QrCode, Power, ShieldAlert, RefreshCw } from "lucide-react"

/**
 * Conexão do WhatsApp do condomínio.
 * O síndico escaneia o QR Code uma única vez e o sistema passa a enviar
 * avisos, encomendas e convocações automaticamente pelo número dele.
 */
export function WhatsAppConnectCard() {
  const { profile } = useAuth()
  const { conectado, estado, carregando, qrCode, conectar, desconectar, recarregar } =
    useWhatsAppConnection()

  const podeGerenciar = ["sindico", "administracao", "superadmin"].includes(profile?.role || "")

  if (!profile?.condominium_id) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" /> WhatsApp do Condomínio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>
              Seu usuário ainda não está vinculado a um condomínio.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" /> WhatsApp do Condomínio
          </CardTitle>
          <div className="flex items-center gap-2">
            {carregando ? (
              <Badge variant="secondary">
                <Loader2 className="mr-1 h-3 w-3 animate-spin" /> verificando
              </Badge>
            ) : conectado ? (
              <Badge className="bg-green-600 hover:bg-green-600">Conectado</Badge>
            ) : (
              <Badge variant="secondary">{estado === "connecting" ? "Aguardando leitura" : "Desconectado"}</Badge>
            )}
            <Button variant="ghost" size="icon" onClick={recarregar} title="Atualizar status">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Conecte o número de WhatsApp do condomínio para que o sistema envie{" "}
          <strong>automaticamente</strong> os avisos de encomenda, comunicados e convocações de
          reunião — sem precisar abrir o WhatsApp e clicar em enviar.
        </p>

        {!podeGerenciar && (
          <Alert>
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>
              Apenas o síndico ou a administração podem conectar o WhatsApp.
            </AlertDescription>
          </Alert>
        )}

        {qrCode && !conectado && (
          <div className="flex flex-col items-center gap-3 rounded-lg border bg-muted/40 p-4">
            <img
              src={qrCode}
              alt="QR Code para conectar o WhatsApp"
              className="h-56 w-56 rounded-md bg-white p-2"
            />
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li>Abra o WhatsApp no celular do condomínio</li>
              <li>
                Toque em <strong>Aparelhos conectados</strong> → <strong>Conectar aparelho</strong>
              </li>
              <li>Aponte a câmera para este QR Code</li>
            </ol>
            <p className="text-xs text-muted-foreground">
              A tela atualiza sozinha assim que a conexão for concluída.
            </p>
          </div>
        )}

        {conectado && (
          <Alert>
            <MessageCircle className="h-4 w-4" />
            <AlertDescription>
              Tudo pronto! As mensagens do sistema já saem pelo WhatsApp do condomínio.
            </AlertDescription>
          </Alert>
        )}

        {podeGerenciar && (
          <div className="flex flex-wrap gap-2">
            {!conectado && (
              <Button onClick={() => conectar.mutate()} disabled={conectar.isPending}>
                {conectar.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <QrCode className="mr-2 h-4 w-4" />
                )}
                {qrCode ? "Gerar novo QR Code" : "Conectar WhatsApp"}
              </Button>
            )}

            {conectado && (
              <Button
                variant="outline"
                onClick={() => {
                  if (confirm("Desconectar o WhatsApp do condomínio? Os envios automáticos vão parar.")) {
                    desconectar.mutate()
                  }
                }}
                disabled={desconectar.isPending}
              >
                {desconectar.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Power className="mr-2 h-4 w-4" />
                )}
                Desconectar
              </Button>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          O sistema respeita um intervalo entre as mensagens para proteger o número contra bloqueio
          do WhatsApp.
        </p>
      </CardContent>
    </Card>
  )
}
